/**
 * Bill Validation — what the Bulk Bill Run board's bulk actions will do for a
 * selection, before anything is sent. Every bill ends up either in the plan or
 * in `skipped` with a reason the confirm step shows — nothing drops silently.
 */

import type { BvBill } from '../types/billTypes';
import type { ParseState } from '../utils/parseState';

export type Skip = { billId: string; reason: string };

export type ParsePlan = {
  /** Emails for the inbox re-parse pool (/inbox/retry-selected). */
  inboxEmailIds: string[];
  inboxBillIds: string[];
  /** Hand-uploaded invoices, parsed one request per bill. */
  direct: Array<{ billId: string; storagePath: string }>;
  skipped: Skip[];
};

export function planParse(
  rows: Array<{ bill: BvBill; parseState: ParseState; emailIds: string[] }>,
): ParsePlan {
  const plan: ParsePlan = { inboxEmailIds: [], inboxBillIds: [], direct: [], skipped: [] };
  for (const { bill, parseState, emailIds } of rows) {
    if (parseState === 'parsed') { plan.skipped.push({ billId: bill.id, reason: 'Already parsed' }); continue; }
    if (parseState === 'parsing') { plan.skipped.push({ billId: bill.id, reason: 'Parsing now' }); continue; }
    if (emailIds.length > 0) {
      // The inbox pool re-parses the email's attachments into this bill — the
      // same path an arriving invoice takes.
      plan.inboxEmailIds.push(...emailIds);
      plan.inboxBillIds.push(bill.id);
    } else if (bill.invoice_path) {
      plan.direct.push({ billId: bill.id, storagePath: bill.invoice_path });
    } else {
      plan.skipped.push({ billId: bill.id, reason: 'No invoice attached' });
    }
  }
  return plan;
}

export type RunPlan = { billIds: string[]; skipped: Skip[] };

/**
 * Bills to send to "Run bill run". Only what the page can rule out alone;
 * the server applies the nightly job's own admission — usage data must cover
 * the whole period — and returns its reasons for anything it skips.
 */
export function planRun(bills: BvBill[]): RunPlan {
  const plan: RunPlan = { billIds: [], skipped: [] };
  for (const bill of bills) {
    if (!bill.bill_from || !bill.bill_to) plan.skipped.push({ billId: bill.id, reason: 'No bill period' });
    else if (bill.bill_run_status === 'running') plan.skipped.push({ billId: bill.id, reason: 'Already running' });
    else plan.billIds.push(bill.id);
  }
  return plan;
}
